import { MAX_ROW_LIMIT, buildResource, runQueries } from '../GscTools/query';
import { SEGMENT, pageFilters } from './segments';

/**
 * The four requests this report makes.
 *
 * Each block owns one, and each is sent on its own rather than batched, so a failure is confined
 * to the panel that caused it: an appearance list Search Console will not return for this
 * property must not take the country table down with it.
 *
 * Segment rows are not paged. Countries, devices and appearances are small, closed-ish sets — a
 * few hundred rows at the very most — so one request at the API's own ceiling retrieves the whole
 * partition, and a "load more" button would imply there is more to come when there is not.
 */

/** The scope's filters, as every block that honours them sends them. */
function scopeFilters(scope) {
    return {
        searchType: scope.searchType,
        country: scope.country,
        device: scope.device,
        dataState: scope.dataState,
        filters: pageFilters(scope),
    };
}

/**
 * The filters the segment table and its trend share.
 *
 * Keyed by appearance they are nothing at all: Search Console will not combine searchAppearance
 * with another dimension, and a trend filtered differently from the row it was opened from would
 * be a different question with the same heading.
 */
function segmentFilters(scope) {
    if (!scope.takesFilters) {
        return { searchType: scope.searchType, dataState: scope.dataState, filters: [] };
    }
    return scopeFilters(scope);
}

/** Block 01 — one period of rows keyed by the selected segment dimension. */
export async function fetchSegmentRows({ credentialsDomainId, scope, period }) {
    const results = await runQueries({
        credentialsDomainId,
        siteUrl: scope.siteUrl,
        maxCount: MAX_ROW_LIMIT,
        queries: [
            {
                key: 'segments',
                resource: buildResource({
                    period,
                    dimensions: [scope.segment],
                    aggregationType: 'auto',
                    ...segmentFilters(scope),
                }),
            },
        ],
    });

    return results.segments ?? [];
}

/**
 * Block 02 — the daily series for one segment key.
 *
 * It is a separate filtered request rather than a regrouping of block 01's rows: those were
 * returned keyed by segment with no date dimension, so the days inside them were never sent. The
 * key is passed back exactly as Search Console returned it, which is how its `equals` filter
 * compares.
 */
export async function fetchSegmentSeries({ credentialsDomainId, scope, period, segmentKey }) {
    const { filters = [], ...rest } = segmentFilters(scope);

    const results = await runQueries({
        credentialsDomainId,
        siteUrl: scope.siteUrl,
        maxCount: MAX_ROW_LIMIT,
        queries: [
            {
                key: 'daily',
                resource: buildResource({
                    period,
                    dimensions: ['date'],
                    aggregationType: 'auto',
                    ...rest,
                    filters: [
                        ...filters,
                        {
                            dimension: scope.segment,
                            operator: 'equals',
                            expression: segmentKey,
                        },
                    ],
                }),
            },
        ],
    });

    return results.daily ?? [];
}

/**
 * Block 03, first request — which search appearances this property returned for this window.
 *
 * Sent with the searchAppearance dimension and nothing else: no second dimension, and no filters
 * either. The API will not combine this dimension with another one, and the point of the call is
 * to discover the keys that actually exist before any of them is offered for selection — a list
 * assembled from Google's documented features instead would offer appearances this property has
 * never had.
 */
export async function fetchAppearances({ credentialsDomainId, scope, period }) {
    const results = await runQueries({
        credentialsDomainId,
        siteUrl: scope.siteUrl,
        maxCount: MAX_ROW_LIMIT,
        queries: [
            {
                key: 'appearances',
                resource: buildResource({
                    period,
                    dimensions: [SEGMENT.APPEARANCE],
                    aggregationType: 'auto',
                    searchType: scope.searchType,
                    dataState: scope.dataState,
                }),
            },
        ],
    });

    return results.appearances ?? [];
}

/**
 * Block 03, second request — the pages that carried one discovered appearance.
 *
 * Here searchAppearance is a filter rather than a dimension, so the scope's own country, device
 * and page filters travel with it. That is deliberately not what the discovery call above does,
 * and the block says so: the list of appearances is what the property returned, while the
 * contributors under one of them are what it returned inside the scope on screen.
 */
export async function fetchAppearancePages({ credentialsDomainId, scope, period, appearance }) {
    const { filters = [], ...rest } = scopeFilters(scope);

    const results = await runQueries({
        credentialsDomainId,
        siteUrl: scope.siteUrl,
        maxCount: MAX_ROW_LIMIT,
        queries: [
            {
                key: 'pages',
                resource: buildResource({
                    period,
                    dimensions: ['page'],
                    aggregationType: 'auto',
                    ...rest,
                    filters: [
                        ...filters,
                        {
                            dimension: SEGMENT.APPEARANCE,
                            operator: 'equals',
                            expression: appearance,
                        },
                    ],
                }),
            },
        ],
    });

    return results.pages ?? [];
}
